import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { toast } from "sonner";
import { format, addDays, subDays } from "date-fns";
import {
  FunnelSimple,
  MagnifyingGlass,
  ArrowsClockwise,
  CalendarBlank,
  SortAscending,
  X,
} from "phosphor-react";
import {
  TechVisitCard,
  TechEmptyState,
  TechLoadingState,
} from "../../components/TechComponents";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TechVisits() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [priority, setPriority] = useState(searchParams.get("priority") || "all");
  const [serviceType, setServiceType] = useState(searchParams.get("service_type") || "all");
  const [dateRange, setDateRange] = useState(searchParams.get("date_range") || "all");
  const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "scheduled_start");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sort_order") || "asc");

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Date range
      const now = new Date();
      if (dateRange === "today") {
        params.set("start_date", format(now, "yyyy-MM-dd"));
        params.set("end_date", format(now, "yyyy-MM-dd"));
      } else if (dateRange === "week") {
        params.set("start_date", format(now, "yyyy-MM-dd"));
        params.set("end_date", format(addDays(now, 7), "yyyy-MM-dd"));
      } else if (dateRange === "overdue") {
        params.set("end_date", format(subDays(now, 1), "yyyy-MM-dd"));
      }

      if (status && status !== "all") params.set("status", status);
      if (priority && priority !== "all") params.set("priority", priority);
      if (serviceType && serviceType !== "all") params.set("service_type", serviceType);
      if (search) params.set("search", search);
      params.set("sort_by", sortBy);
      params.set("sort_order", sortOrder);
      params.set("limit", "100");

      const response = await fetch(`${API}/tech/visits?${params.toString()}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setVisits(data.visits || []);
        setTotal(data.total || 0);
      } else {
        toast.error("Failed to load visits");
      }
    } catch (error) {
      console.error("Visits fetch error:", error);
      toast.error("Failed to load visits");
    } finally {
      setLoading(false);
    }
  }, [dateRange, status, priority, serviceType, search, sortBy, sortOrder]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status && status !== "all") params.set("status", status);
    if (priority) params.set("priority", priority);
    if (serviceType) params.set("service_type", serviceType);
    if (dateRange !== "all") params.set("date_range", dateRange);
    if (sortBy !== "scheduled_start") params.set("sort_by", sortBy);
    if (sortOrder !== "asc") params.set("sort_order", sortOrder);
    setSearchParams(params);
  }, [search, status, priority, serviceType, dateRange, sortBy, sortOrder, setSearchParams]);

  const handleVisitClick = (visit) => {
    navigate(`/tech/visits/${visit.visit_id}`);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setPriority("all");
    setServiceType("all");
    setDateRange("all");
    setSortBy("scheduled_start");
    setSortOrder("asc");
  };

  const hasActiveFilters = search || status !== "all" || priority !== "all" || serviceType !== "all" || dateRange !== "all";

  const activeFilterCount = [search, status !== "all" ? status : "", priority !== "all" ? priority : "", serviceType !== "all" ? serviceType : "", dateRange !== "all" ? dateRange : ""].filter(Boolean).length;

  return (
    <div className="space-y-4 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight">
            My Visits
          </h1>
          <p className="text-zinc-500">{total} visit(s) found</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchVisits}
          className="border-2"
          disabled={loading}
        >
          <ArrowsClockwise size={18} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs, customers..."
            className="pl-10 h-12 border-2"
          />
        </div>

        {/* Mobile Filter Button */}
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-12 px-4 border-2 lg:hidden relative">
              <FunnelSimple size={20} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF6600] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle className="font-heading uppercase">Filters</SheetTitle>
            </SheetHeader>
            <FilterControls
              status={status}
              setStatus={setStatus}
              priority={priority}
              setPriority={setPriority}
              serviceType={serviceType}
              setServiceType={setServiceType}
              dateRange={dateRange}
              setDateRange={setDateRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              onClear={clearFilters}
              onApply={() => setFiltersOpen(false)}
            />
          </SheetContent>
        </Sheet>

        {/* Desktop Filter Button */}
        <div className="hidden lg:flex gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40 h-12 border-2">
              <CalendarBlank size={18} className="mr-2" />
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dates</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-12 border-2">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="en_route">En Route</SelectItem>
              <SelectItem value="on_site">On Site</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            className="h-12 border-2"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          >
            <SortAscending size={18} className={sortOrder === "desc" ? "rotate-180" : ""} />
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="h-12">
              <X size={18} className="mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters (Mobile) */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 lg:hidden">
          {status !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Status: {status}
              <button onClick={() => setStatus("all")}><X size={12} /></button>
            </Badge>
          )}
          {priority !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Priority: {priority}
              <button onClick={() => setPriority("all")}><X size={12} /></button>
            </Badge>
          )}
          {dateRange !== "all" && (
            <Badge variant="secondary" className="gap-1">
              {dateRange}
              <button onClick={() => setDateRange("all")}><X size={12} /></button>
            </Badge>
          )}
          <button
            onClick={clearFilters}
            className="text-sm text-[#FF6600] font-medium"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Visits List */}
      {loading ? (
        <TechLoadingState message="Loading visits..." />
      ) : visits.length > 0 ? (
        <div className="space-y-3">
          {visits.map((visit) => (
            <TechVisitCard
              key={visit.visit_id}
              visit={visit}
              onClick={() => handleVisitClick(visit)}
              showDate
            />
          ))}
        </div>
      ) : (
        <TechEmptyState
          icon={CalendarBlank}
          title="No visits found"
          description={hasActiveFilters ? "Try adjusting your filters" : "You have no assigned visits"}
          action={
            hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )
          }
        />
      )}
    </div>
  );
}

// Filter Controls Component
function FilterControls({
  status,
  setStatus,
  priority,
  setPriority,
  serviceType,
  setServiceType,
  dateRange,
  setDateRange,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  onClear,
  onApply,
}) {
  return (
    <div className="space-y-6 py-6">
      <div>
        <Label className="text-sm font-medium">Date Range</Label>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue placeholder="All Dates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dates</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium">Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="en_route">En Route</SelectItem>
            <SelectItem value="on_site">On Site</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium">Priority</Label>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium">Service Type</Label>
        <Select value={serviceType} onValueChange={setServiceType}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="plumbing">Plumbing</SelectItem>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium">Sort By</Label>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled_start">Scheduled Time</SelectItem>
            <SelectItem value="created_at">Created Date</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium">Sort Order</Label>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="mt-2 h-12 border-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">Ascending (Earliest First)</SelectItem>
            <SelectItem value="desc">Descending (Latest First)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onClear} className="flex-1 border-2">
          Clear All
        </Button>
        <Button onClick={onApply} className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white">
          Apply Filters
        </Button>
      </div>
    </div>
  );
}
